import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import UserAvatar from '../ui/UserAvatar';
import { getApiErrorMessage } from '../../utils/apiError';

function primaryPhone(user) {
  if (Array.isArray(user?.phone_numbers) && user.phone_numbers.length) {
    return user.phone_numbers[0] || '';
  }
  return '';
}

function AdminEditUserModal({
  isOpen,
  onClose,
  user,
  t,
  formatMessage,
  onSaved,
}) {
  const a = t.admin || {};
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  useEffect(() => {
    if (!isOpen || !user) return;
    setUsername(user.username || '');
    setEmail(user.email || '');
    setPhone(primaryPhone(user));
    setPassword('');
  }, [isOpen, user]);

  const roleLabel = useMemo(() => {
    if (!user) return '';
    if (user.is_admin) return t.common?.admin || 'Admin';
    if (user.is_vendor) return a.vendorRole || 'Representative';
    if (user.is_client) return a.clients || 'Client';
    return user.user_type || '';
  }, [user, t.common?.admin, a.vendorRole, a.clients]);

  if (!user) return null;

  const userType = user.user_type || (user.is_admin ? 'admin' : user.is_vendor ? 'vendor' : 'client');

  const handleSave = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        username: username.trim(),
        email: email.trim(),
        phone: phone.trim(),
      };
      if (password.trim()) {
        payload.password = password.trim();
      }
      const response = await axios.put(
        `/api/admin/users/${userType}/${user.id}`,
        payload,
        { withCredentials: true },
      );
      toast.success(
        response.data?.email_sent
          ? (a.userUpdatedAndNotified || 'User updated and notified by email')
          : (a.userUpdated || 'User updated successfully'),
      );
      setPassword('');
      onSaved?.(response.data?.user || {
        ...user,
        username: payload.username,
        email: payload.email,
        phone_numbers: [payload.phone],
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, a.userUpdateFailed || 'Failed to update user'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendReset = async () => {
    if (sendingReset) return;
    setSendingReset(true);
    try {
      const response = await axios.post(
        `/api/admin/users/${userType}/${user.id}/send-password-reset`,
        {},
        { withCredentials: true },
      );
      toast.success(
        response.data?.email_sent
          ? (a.passwordResetLinkSent || 'Password reset link sent')
          : (a.passwordResetQueued || 'Password reset requested'),
      );
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, t, formatMessage, a.passwordResetFailed || 'Failed to send reset link'),
      );
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={a.editUser || 'Edit User'}
      size="default"
      footer={(
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving || sendingReset}>
            {t.common?.cancel || 'Cancel'}
          </Button>
          <Button type="submit" form="admin-edit-user-form" loading={saving} disabled={sendingReset}>
            {a.saveUserChanges || 'Save changes'}
          </Button>
        </div>
      )}
    >
      <form id="admin-edit-user-form" className="space-y-5" onSubmit={handleSave}>
        <div className="flex items-center gap-4">
          <UserAvatar
            src={user.avatar_url}
            alt={username || user.username}
            size="lg"
            variant="brand"
            className="shadow-glow shrink-0"
          />
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold text-secondary-900 ">
              {username || user.username}
            </p>
            <p className="text-sm text-secondary-500 ">{roleLabel}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-secondary-700 ">
              {a.fullName || a.username || 'Name'}
            </label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              maxLength={80}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-secondary-700 ">
              {a.email || 'Email'}
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={120}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-secondary-700 ">
              {a.phone || 'Phone'}
            </label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              maxLength={30}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-secondary-700 ">
              {a.setTemporaryPassword || 'Set temporary password (optional)'}
            </label>
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={password ? 6 : undefined}
              placeholder={a.leaveBlankToKeepPassword || 'Leave blank to keep current password'}
              autoComplete="new-password"
            />
            <p className="mt-1 text-xs text-secondary-500 ">
              {a.temporaryPasswordHint || 'If set, the user will be emailed the new password.'}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-secondary-200 bg-secondary-50/70 p-4  ">
          <p className="text-sm font-medium text-secondary-800 ">
            {a.resetPasswordSectionTitle || 'Password reset link'}
          </p>
          <p className="mt-1 text-sm text-secondary-600 ">
            {a.resetPasswordSectionHint || 'Send a secure link so the user can choose a new password.'}
          </p>
          <Button
            type="button"
            variant="secondary"
            className="mt-3"
            loading={sendingReset}
            disabled={saving}
            onClick={handleSendReset}
          >
            {a.sendPasswordResetLink || 'Send reset link'}
          </Button>
        </div>

        {(user.referrals_count != null || user.referred_by_username) && (
          <div className="grid grid-cols-1 gap-3 border-t border-secondary-200 pt-4 sm:grid-cols-2 ">
            {user.referrals_count != null ? (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{a.totalReferrals}</p>
                <p className="mt-1 text-lg font-semibold text-orange-600">{user.referrals_count || 0}</p>
              </div>
            ) : null}
            {user.referred_by_username ? (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{a.referredBy}</p>
                <p className="mt-1 text-sm text-secondary-900 ">{user.referred_by_username}</p>
              </div>
            ) : null}
          </div>
        )}
      </form>
    </Modal>
  );
}

export default AdminEditUserModal;
